import { Twitter, Instagram, Youtube, Globe, Music2, Film, ExternalLink, Sparkles } from "lucide-react";
import { GlassCard } from "@/components/common/GlassCard";

// ---------------------------------------------------------------------------
// Phase 14 — Social & Film embeds (Layer 4 · Family / Layer 1 · Eval)
// ---------------------------------------------------------------------------
// Lightweight, opt-in embed surface. Hudl + YouTube get true iframe embeds
// because both have fully-public oEmbed-style endpoints. Twitter/X, Instagram,
// and TikTok don't iframe cleanly without first-party scripts (and Instagram
// blocks unauthenticated embeds entirely as of 2024) — we render those as
// branded link tiles to keep the code small and the page fast.
// ---------------------------------------------------------------------------

const SOCIAL_META = {
  twitter:   { icon: Twitter,   label: "Twitter / X", color: "text-[#1DA1F2]" },
  instagram: { icon: Instagram, label: "Instagram",    color: "text-[#E1306C]" },
  tiktok:    { icon: Music2,    label: "TikTok",       color: "text-[#69C9D0]" },
  youtube:   { icon: Youtube,   label: "YouTube",      color: "text-[#FF0033]" },
  hudl:      { icon: Film,      label: "Hudl",         color: "text-[#FF6300]" },
  website:   { icon: Globe,     label: "Website",      color: "text-cyan-300"  },
};

const ORDER = ["hudl", "youtube", "twitter", "instagram", "tiktok", "website"];

/**
 * Best-effort YouTube ID extractor (watch?v=, youtu.be/, embed/).
 * Returns null for malformed URLs so the caller can fall back to a link tile.
 */
function youtubeEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname === "youtu.be") {
      const id = u.pathname.replace("/", "");
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname.startsWith("/embed/")) return url;
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
      // /shorts/{id}
      const m = u.pathname.match(/^\/shorts\/([\w-]+)/);
      if (m) return `https://www.youtube.com/embed/${m[1]}`;
    }
  } catch { /* ignore */ }
  return null;
}

/**
 * Hudl embed URL — Hudl supports `<iframe src="https://www.hudl.com/embed/video/{id}">`.
 * Most copy-paste Hudl URLs look like:
 *   https://www.hudl.com/video/3/{teamId}/{videoId}
 *   https://www.hudl.com/embed/video/{videoId}
 * We treat already-embed URLs as-is and try to convert /video/3/x/{id} variants.
 */
function hudlEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (!u.hostname.includes("hudl.com")) return null;
    if (u.pathname.startsWith("/embed/")) return url;
    // /video/3/{team}/{videoId}
    const parts = u.pathname.split("/").filter(Boolean);
    const last = parts[parts.length - 1];
    if (last && /^[a-z0-9-]+$/i.test(last)) {
      return `https://www.hudl.com/embed/video/${last}`;
    }
  } catch { /* ignore */ }
  return null;
}

export function SocialEmbeds({ social = {}, compact = false }) {
  const present = ORDER.filter((k) => social[k]);
  if (!present.length) return null;

  const ytEmbed = youtubeEmbedUrl(social.youtube);
  const hudlEmbed = hudlEmbedUrl(social.hudl);

  return (
    <GlassCard className="p-5" testId="social-embeds">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-cyan-300" />
          <h3 className="text-1 text-sm font-semibold">Social &amp; Film</h3>
        </div>
        <span className="text-3 text-[10px] uppercase tracking-wider">{present.length} link{present.length === 1 ? "" : "s"}</span>
      </div>

      {/* Link chip row */}
      <div className="flex flex-wrap gap-2">
        {present.map((k) => {
          const m = SOCIAL_META[k];
          const Icon = m.icon;
          return (
            <a
              key={k}
              data-testid={`social-link-${k}`}
              href={social[k]}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] ring-1 ring-white/10 hover:ring-cyan-300/40 transition text-xs text-1"
            >
              <Icon className={`h-3.5 w-3.5 ${m.color}`} />
              <span>{m.label}</span>
              <ExternalLink className="h-3 w-3 text-3" />
            </a>
          );
        })}
      </div>

      {/* Embeds */}
      {!compact && (hudlEmbed || ytEmbed) && (
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
          {hudlEmbed && (
            <div className="aspect-video w-full rounded-lg overflow-hidden ring-1 ring-white/10 bg-black" data-testid="social-embed-hudl">
              <iframe
                src={hudlEmbed}
                title="Hudl highlights"
                allow="autoplay; fullscreen"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          )}
          {ytEmbed && (
            <div className="aspect-video w-full rounded-lg overflow-hidden ring-1 ring-white/10 bg-black" data-testid="social-embed-youtube">
              <iframe
                src={ytEmbed}
                title="YouTube highlights"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          )}
        </div>
      )}
    </GlassCard>
  );
}
